<script setup lang="ts">
/**
 * Компонент для выбора вариантов продукта (табы или radio buttons) — UI-kit.
 * Следует Vue Style Guide: Priority A - Single Responsibility
 */

import { computed } from 'vue';
import { productVariantLabel } from '../lib/productSizeLabel';
import type { ProductVariant } from '../model/types/product.types';

interface Props {
  variants: ProductVariant[];
  modelValue: number;
  useRadioButtons?: boolean;
}

interface Emits {
  (e: 'update:modelValue', value: number): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

// Варианты → items UiKitTabs (ключ = индекс, чтобы сохранить числовую модель)
const variantTabs = computed(() =>
  props.variants.map((variant, idx) => ({ key: idx, label: productVariantLabel(variant) })),
);

// Локальное значение для v-model (числовой индекс варианта)
const localValue = computed({
  get: () => props.modelValue,
  set: (value: number | string) => emit('update:modelValue', Number(value)),
});
</script>

<template>
  <div v-if="variants.length > 1" class="product-variant-selector">
    <!-- Radio-версия (много вариантов) -->
    <UiKitRadioGroup
      v-if="useRadioButtons"
      v-model="localValue"
      color="default"
      class="product-variant-selector__radio"
    >
      <UiKitRadio
        v-for="(variant, idx) in variants"
        :key="variant.id"
        :value="idx"
      >
        {{ productVariantLabel(variant) }}
      </UiKitRadio>
    </UiKitRadioGroup>

    <!-- Табы-версия -->
    <UiKitTabs
      v-else
      v-model="localValue"
      :items="variantTabs"
      variant="solid"
      color="default"
      size="lg"
      full-width
      :panel="false"
      class="product-variant-selector__tabs"
    />
  </div>
</template>

<style scoped lang="scss">
.product-variant-selector {
  @apply w-full;

  &__radio {
    @apply my-4;
  }

  // Бордер у контейнера табов — как у строк/табов корзины (BasketRow / MethodReceipt)
  &__tabs :deep([role='tablist']) {
    @apply border border-hui-divider;
  }

  // Длинные названия вариантов («Свино-Говяжей») переносим на новую строку:
  // в UiKitTabs у таба зашиты whitespace-nowrap + фикс-высота h-11 → текст обрезался.
  &__tabs :deep([role='tab']) {
    height: auto;
    min-height: 2.75rem; // = h-11
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
    white-space: normal;
    line-height: 1.15;
    text-align: center;
    overflow-wrap: anywhere;

    // На 1024–1279 правая колонка модалки ~430px на две-три вкладки: с базовым
    // text-base «Стандартная» переносилась на вторую строку прямо посреди слова.
    // Кегль на ступень мельче, с 1280px — прежний.
    @apply text-sm xl:text-base;
    @apply px-2 xl:px-4;
  }
}
</style>
